import {ChangeDetectorRef, Component, HostListener, Inject, OnInit, ViewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatSelectionList, MatSelectionListChange} from '@angular/material/list';
import {MatRadioChange} from '@angular/material/radio';
import {filePath, SwalService, UserService} from '@atmi/core';
import {collectionItems$} from '@atmi/store/collection-item.repository';
import {collections$} from '@atmi/store/collection.repository';
import {
  CardFiltersProps,
  CardTypeFilter,
  cardFilters$,
  defaultFilters,
  updateCardType,
  updateFilters,
  updateMaxPrice,
  updateMinPrice,
  updateText
} from '@atmi/store/card-filters.repository';
import {CollectionItem} from '@sim/collections/models/collection';
import {CollectionItemApiService} from '@sim/collections/services';
import {filteredCards$} from '@src/app/store/oa.repository';
import {ICompressedCard, SearchIndexingService} from '@ui/search-indexing.service';
import {firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';

@Component({
  selector: 'app-nav-fab-menu',
  styleUrls: ['./nav-fab-menu.component.scss'],
  templateUrl: './nav-fab-menu.component.html',
})
export class NavFabMenuComponent implements OnInit {

  isOpen = false;
  searchIsOpen = false;
  searchResults: ICompressedCard[] = [];
  searchText = '';
  minPrice: number | null = null;
  maxPrice: number | null = null;
  searchType: 'text' | 'tokens' = 'text';
  searchReady = false;
  cardFilters: CardFiltersProps = {...defaultFilters};
  selectedCards: ICompressedCard[] = [];
  selectedCollections: CollectionItem[] = [];
  addingToCollection = false;
  selectedCardType: CardTypeFilter = 'oa';

  @ViewChild(MatSelectionList) collectionsList?: MatSelectionList;

  readonly collections = toSignal(collections$, {initialValue: []});
  readonly collectionItems = toSignal(collectionItems$, {initialValue: []});

  constructor(
    private readonly collectionItemsApi: CollectionItemApiService,
    private searchService: SearchIndexingService,
    protected readonly swalService: SwalService,
    private cdr: ChangeDetectorRef,
    @Inject(filePath) readonly fileUrl: string,
    private readonly userService: UserService) {
    filteredCards$.subscribe(filteredCards => {
      /*first filter parents and children separately
      * then sort parents by name*/
      const parents = filteredCards.filter(card => card.isParent);
      const children = filteredCards.filter(card => !card.isParent);
      parents.sort((a, b) => a.sgt > b.sgt ? 1 : -1);
      children.sort((a, b) => a.sgt > b.sgt ? 1 : -1);
      const childrenMapByParent: Record<string, ICompressedCard[]> = Object.fromEntries(parents.map(card => [card.id, [card]]));
      children.forEach(card => {
        if (card.operaMadre in childrenMapByParent) {
          childrenMapByParent[card.operaMadre].push(card);
        }
      });

      this.searchResults = Object.values(childrenMapByParent).flat();
    });

    cardFilters$.subscribe(filters => {
      this.cardFilters = filters;
      this.selectedCardType = filters.cardType;
    });
  }

  ngOnInit(): void {
    this.initSearch();
  }

  addToCollections = async () => {
    this.addingToCollection = true;
    for (const collection of this.selectedCollections) {
      for (const card of this.selectedCards) {
        try {
          // Map card type to className
          let className: 'BeneOA' | 'BeneF' | 'BeneS' | 'BeneD' | 'BeneMI' = 'BeneOA';
          switch (card.cardType) {
            case 'oa':
              className = 'BeneOA';
              break;
            case 'f':
              className = 'BeneF';
              break;
            case 's':
              className = 'BeneS';
              break;
            case 'd':
              className = 'BeneD';
              break;
            case 'mi':
              className = 'BeneMI';
              break;
          }

          const res = await firstValueFrom(this.collectionItemsApi.post(collection.id, {
            className,
            objectId: card.id,
          }));
        } catch (e) {
          console.log(e);
        }
      }
    }
    this.addingToCollection = false;
    this.swalService.showSuccess({title: 'Operazione completata', text: 'Oggetti aggiunti con successo alle collezioni'});
  };

  handleSearchTypeChange($event: MatRadioChange) {
    updateFilters({
      ...this.cardFilters,
      tokenized: $event.value === 'tokens',
      tokens: this.searchText.split(' ').map(t => t.trim())
    });
  }

  handleCardTypeChange(cardType: CardTypeFilter) {
    this.selectedCardType = cardType;
    updateCardType(cardType);
  }

  async initSearch() {
    // Check if user is authenticated before initializing search
    const userState = await firstValueFrom(this.userService.selectState().pipe(
      map(state => state.user)
    ));

    if (!userState) {
      console.log('User not authenticated, skipping search initialization');
      return;
    }

    setTimeout(async () => {
      try {
        this.searchReady = await this.searchService.init();
      } catch (error) {
        console.warn('Search initialization failed:', error);
        this.searchReady = false;
      }
    }, 1000);
  }

  handleTextSearchChanged($event: any) {
    const text = $event.target.value.trim();
    if (text.length > 0) {
      this.searchText = text;
    } else {
      this.searchText = '';
    }
    this.applyFilters(this.searchText);
  }

  handleMinPriceChanged(_$event: Event) {
    updateMinPrice(this.minPrice);
  }

  handleMaxPriceChanged(_$event: Event) {
    updateMaxPrice(this.maxPrice);
  }

  applyFilters = (text: string = '') => {
    updateText(text);
  }

  onMouseEnter() {
    this.isOpen = true;
  }

  onMouseOut() {
    this.isOpen = false;
  }

  toggleIsOpen() {
    this.isOpen = !this.isOpen;
  }

  toggleSearchOpen() {
    this.searchIsOpen = !this.searchIsOpen;
    if (this.searchIsOpen) {
      this.cdr.detectChanges();
    }
  }

  handleListSelectionChange($event: MatSelectionListChange) {
    console.log({$event});
  }

  handleCollectionsListSelectionChange($event: MatSelectionListChange) {
    console.log({$event});
  }

  deselectAllCollections() {
    if (this.collectionsList) {
      this.collectionsList.deselectAll();
    }
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscapeHandler($event: KeyboardEvent) {
    if (this.searchIsOpen) {
      $event.preventDefault();
      this.searchIsOpen = false;
    }
  }

  /*
  * https://medium.com/claritydesignsystem/angular-pseudo-events-d4e7f89247ee
  * */
  @HostListener('window:keydown.control.f', ['$event']) test($event: KeyboardEvent) {
    if (!this.searchIsOpen && this.isOpen && this.searchReady) {
      $event.preventDefault();
      this.searchIsOpen = true;
      this.cdr.detectChanges();
    }
  }


}


/*
* https://dev.to/thormeier/algorithm-explained-text-similarity-using-a-vector-space-model-3bog
* https://textbooks.math.gatech.edu/ila/similarity.html
* */
